import { FC } from "react";
import { twMerge } from "tailwind-merge";

import { getOfficialSubmission } from "components/materials/page/helpers";
import type { SubmissionType } from "components/server-types";
import { t } from "i18n/i18n";

interface NewSubmissionBadgeProps {
  /** Submissions for the student/group, sorted descending by created_at (latest first) */
  sortedSubmissions: SubmissionType[];
  className?: string;
}

/**
 * Displays a "NEW" badge if there's an unseen submission after the official (assessed) one.
 */
export const NewSubmissionBadge: FC<NewSubmissionBadgeProps> = ({
  sortedSubmissions,
  className,
}) => {
  const officialSubmission = getOfficialSubmission(sortedSubmissions);
  if (!officialSubmission) return null;

  // Since submissions are sorted descending by created_at, the first one is the latest
  const latestSubmission = sortedSubmissions[0];
  if (!latestSubmission) return null;

  // Check if latest submission is newer than the official one and hasn't been seen
  const isNewerThanOfficial =
    new Date(latestSubmission.created_at) > new Date(officialSubmission.created_at);

  if (!isNewerThanOfficial || latestSubmission.seen_after_assessed_at) {
    return null;
  }

  return (
    <span className={twMerge("shrink-0 text-sm font-bold uppercase text-blue", className)}>
      {t("student_assessment_queue.new_submission")}
    </span>
  );
};
